import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import GeoSuggest from 'react-geosuggest';
import { variant } from 'styled-system';
import { transparentize } from 'polished';

import Labelize from '../../molecules/form/Labelize';

import { func, string } from 'prop-types';
import InputField from '../../atoms/inputField/InputField';
import territorySwitch from '../../territory/territorySwitch';

const laLouviere = {
  lat: 50.48657,
  lng: 4.18785,
  radius: 130000
};
const bern = {
  lat: 46.947456,
  lng: 7.451123,
  radius: 250000
};
const bissen = {
  lat: 49.7875,
  lng: 6.06944,
  radius: 50000
};
const coordinates = territorySwitch({ io: laLouviere, ch: bern, lu: bissen });

const StyledFakeGeoSuggest = styled(InputField)(() => ({
  width: '100%'
}));

const StyledGeoSuggest = styled(GeoSuggest)(
  ({ theme }) => ({
    '& input': {
      width: '100%',
      height: '40px',
      display: 'inline-block',
      outline: 'none',
      lineHeight: '22px',
      borderRadius: '11px',
      paddingLeft: '16px',
      paddingRight: '16px',
      marginTop: '28px',
      marginLeft: 0,
      border: 'none',
      boxShadow: '0 0 0 1px #f1f1f1 inset',
      color: theme.colors.dark,
      boxSizing: 'border-box',
      backgroundColor: theme.colors.greyVerylight,
      transition: 'box-shadow .3s ease',
      appearance: 'none',
      '&::placeholder': {
        color: theme.colors.grey
      }
    }
  }),
  ({ theme }) =>
    variant({
      prop: 'state',
      variants: {
        success: {
          '& input': {
            boxShadow: `0 0 0 2px ${theme.colors.blue} inset`,
            backgroundColor: transparentize(0.9, theme.colors.blue)
          }
        },
        error: {
          '& input': {
            boxShadow: `0 0 0 2px ${theme.colors.red} inset`,
            backgroundColor: transparentize(0.9, theme.colors.red)
          }
        }
      }
    })
);

const GeoCode = React.forwardRef(
  ({ name, label, errorMessage, onChange, placeholder, defaultValue, ...props }, _ref) => {
    const geoSuggestRef = useRef(null);
    const [isGoogleLoaded, setGoogleLoaded] = useState(!!window.google);
    const [valueInFakeField, setValueInFakeField] = useState(defaultValue);
    const [fakeFieldHasFocus, setFakeFieldHasFocus] = useState(false);
    useEffect(() => {
      if (!isGoogleLoaded && fakeFieldHasFocus) {
        const script = document.createElement('script');
        script.src =
          'https://maps.googleapis.com/maps/api/js?key=AIzaSyBHHbDbcO4NkH0GwLtxGMBoizCTJOWRnuo&libraries=places&language=en';
        script.async = true;
        document.body.appendChild(script);
        script.onload = () => {
          setGoogleLoaded(true);
        };
      }
    }, [isGoogleLoaded, fakeFieldHasFocus]);

    useEffect(() => {
      const fakeFieldHadFocus = isGoogleLoaded && fakeFieldHasFocus;
      if (fakeFieldHadFocus) {
        geoSuggestRef.current.focus();
      }
    }, [isGoogleLoaded, fakeFieldHasFocus]);

    return isGoogleLoaded ? (
      <Labelize label={label} errorMessage={errorMessage} inputName={name}>
        <StyledGeoSuggest
          {...props}
          initialValue={valueInFakeField}
          name={name}
          placeholder={placeholder}
          ref={geoSuggestRef}
          onSuggestSelect={GoogleMapsResult => onChange(GoogleMapsResult?.label)}
          onBlur={onChange}
          onChange={onChange}
          location={new window.google.maps.LatLng(coordinates.lat, coordinates.lng)}
          radius={coordinates.radius}
        />
      </Labelize>
    ) : (
      <Labelize label={label} errorMessage={errorMessage} inputName={name}>
        <StyledFakeGeoSuggest
          {...props}
          className="geosuggest__input" //For cypress
          placeholder={placeholder}
          defaultValue={defaultValue}
          name={name}
          onFocus={() => setFakeFieldHasFocus(true)}
          onBlur={e => {
            setFakeFieldHasFocus(false);
            onChange(e);
          }}
          onChange={e => {
            setValueInFakeField(e.target.value);
            onChange(e);
          }}
        />
      </Labelize>
    );
  }
);

export { GeoCode };

GeoCode.propTypes = {
  name: string,
  label: string,
  placeholder: string,
  errorMessage: string,
  onChange: func,
  defaultValue: string
};

GeoCode.defaultProps = {
  name: 'location',
  onChange: () => null,
  t: () => ''
};

export default GeoCode;
